/**
 * UserMenu Component
 *
 * Dropdown menu showing user information and actions.
 * Displays user email, role badge, change password link, and logout button.
 */

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import type { Role } from '../../../../shared/types';

/**
 * Role badge styling configuration
 */
const ROLE_STYLES: Record<Role, { bg: string; color: string; label: string }> = {
  SUPER_ADMIN: {
    bg: '#7c3aed',
    color: '#ffffff',
    label: 'Super Admin',
  },
  ADMIN: {
    bg: '#dc2626',
    color: '#ffffff',
    label: 'Admin',
  },
  READONLY_ADMIN: {
    // Slate grey — visually distinct from the "active" admin red, signals
    // the role is admin-tier but observation-only.
    bg: '#475569',
    color: '#ffffff',
    label: 'Read-Only Admin',
  },
  COMPANY_MANAGER: {
    bg: '#2563eb',
    color: '#ffffff',
    label: 'Manager',
  },
  COMPANY_USER: {
    bg: '#059669',
    color: '#ffffff',
    label: 'User',
  },
};

export interface UserMenuProps {
  className?: string;
}

export const UserMenu: React.FC<UserMenuProps> = ({ className = '' }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Close menu on escape key
  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  if (!user) {
    return null;
  }

  const roleStyle = ROLE_STYLES[user.role];

  const handleLogout = async () => {
    setIsOpen(false);
    await logout();
  };

  const handleChangePassword = () => {
    setIsOpen(false);
    navigate('/change-password');
  };

  return (
    <div
      ref={menuRef}
      className={className}
      style={{ position: 'relative' }}
    >
      {/* Trigger button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="true"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-3)',
          padding: 'var(--space-1) var(--space-3) var(--space-1) var(--space-1)',
          backgroundColor: 'transparent',
          border: '1px solid var(--line)',
          borderRadius: 'var(--radius-full)',
          color: 'var(--ink)',
          cursor: 'pointer',
          fontFamily: 'var(--font-body)',
          fontSize: 'var(--text-sm)',
          transition: 'background-color var(--transition-fast), border-color var(--transition-fast)',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = 'var(--bg-sunk)';
          e.currentTarget.style.borderColor = 'var(--line-strong)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent';
          e.currentTarget.style.borderColor = 'var(--line)';
        }}
      >
        {/* User avatar */}
        <div
          style={{
            width: '28px',
            height: '28px',
            background: 'linear-gradient(135deg, var(--accent), #a73515)',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: 'var(--font-body)',
            fontWeight: 600,
            fontSize: 'var(--text-xs)',
            textTransform: 'uppercase',
            borderRadius: '50%',
            flexShrink: 0,
          }}
        >
          {user.email.charAt(0)}
        </div>

        {/* User info (hidden on mobile) */}
        <div
          className="sm:block hidden"
          style={{
            textAlign: 'left',
            maxWidth: '150px',
          }}
        >
          <div
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontWeight: 500,
            }}
          >
            {user.email}
          </div>
        </div>

        {/* Dropdown arrow */}
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0)',
            transition: 'transform var(--transition-fast)',
          }}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <div
          role="menu"
          style={{
            position: 'absolute',
            top: 'calc(100% + var(--space-2))',
            right: 0,
            minWidth: '240px',
            backgroundColor: 'var(--bg-elevated)',
            border: '1px solid var(--line)',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-lg)',
            zIndex: 50,
            overflow: 'hidden',
          }}
        >
          {/* User info section */}
          <div
            style={{
              padding: 'var(--space-4)',
              borderBottom: '1px solid var(--line)',
              background: 'var(--bg-sunk)',
            }}
          >
            <div
              style={{
                fontFamily: 'var(--font-body)',
                fontSize: 'var(--text-sm)',
                marginBottom: 'var(--space-2)',
                wordBreak: 'break-all',
                color: 'var(--ink)',
                fontWeight: 500,
              }}
            >
              {user.email}
            </div>

            {/* Role badge */}
            <span
              style={{
                display: 'inline-block',
                padding: '3px var(--space-2)',
                backgroundColor: roleStyle.bg,
                color: roleStyle.color,
                fontFamily: 'var(--font-mono)',
                fontSize: '0.65rem',
                fontWeight: 500,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                borderRadius: 'var(--radius-sm)',
              }}
            >
              {roleStyle.label}
            </span>
          </div>

          {/* Menu items */}
          <div style={{ padding: 'var(--space-2) 0' }}>
            <button
              type="button"
              role="menuitem"
              onClick={handleChangePassword}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-3)',
                width: '100%',
                padding: 'var(--space-3) var(--space-4)',
                backgroundColor: 'transparent',
                border: 'none',
                cursor: 'pointer',
                fontFamily: 'var(--font-body)',
                fontSize: 'var(--text-sm)',
                textAlign: 'left',
                transition: 'background-color var(--transition-fast)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--bg-sunk)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              Change Password
            </button>

            <button
              type="button"
              role="menuitem"
              onClick={handleLogout}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-3)',
                width: '100%',
                padding: 'var(--space-3) var(--space-4)',
                backgroundColor: 'transparent',
                border: 'none',
                cursor: 'pointer',
                fontFamily: 'var(--font-body)',
                fontSize: 'var(--text-sm)',
                textAlign: 'left',
                color: 'var(--color-error)',
                transition: 'background-color var(--transition-fast)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--danger-bg)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
              Sign Out
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserMenu;
